/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Absolute, Box, Flex, Relative } from 'pcln-design-system';
import { Close as CloseIcon, MicOff, Microphone } from 'pcln-icons';
import BotWithAnimatedBorder from './components/Realtime/BotWithAnimatedBorder/BotWithAnimatedBorder';
import RealTimeHeader from './components/Realtime/RealTimeHeader/RealTimeHeader';
import RenderMarkers from './components/Realtime/RenderMarkers/RenderMarkers';
import analytics from './analytics/analytics';
import { useRealTimeChatCoordinator } from './hooks/useRealTimeChatCoordinator';
import { browserStorage } from './utils/browserStorage';
import { useChatMessagesContext } from './hooks/useChatMessagesContext';
import { useLoadingStateContext } from './hooks/useLoadingStateContext';
import { useRealTimeContext } from './hooks/useRealTimeContext';
const Container = /*#__PURE__*/styled(Box).withConfig({
  displayName: "RealTimeAudioPanel__Container",
  componentId: "sc-nq05d4-0"
})(["z-index:1510;height:", ";left:", ";top:", ";width:100%;position:absolute;background:linear-gradient( 144.03deg,#7701bb 26.24%,#7601bb 30.85%,#7503bc 35.32%,#7205bd 39.67%,#6e09bf 43.9%,#690dc1 48.03%,#6312c4 52.1%,#5c18c7 56.1%,#541fca 60.07%,#4c27ce 64.01%,#422fd2 67.94%,#3738d7 71.89%,#2c42dc 75.87%,#204de1 79.89%,#1358e7 83.98%,#0564ed 88.15% ),radial-gradient( 37.63% 77.06% at -3.33% 9.11%,rgba(245,235,250,0.6) 0%,rgba(238,222,246,0.51875) 16.15%,rgba(232,210,243,0.441421) 33.33%,rgba(209,168,232,0.174338) 66.15%,rgba(201,153,228,0.0827664) 82.29%,rgba(194,140,224,0) 100% ),radial-gradient( 22.92% 32.88% at 93.06% 103.5%,rgba(232,242,255,0) 0%,rgba(232,242,255,0) 0.01%,rgba(153,195,249,0) 100% );"], ({
  isModal
}) => isModal ? '100%' : 'calc(100% - 66px)', ({
  isModal
}) => isModal ? 'unset' : '0', ({
  isModal
}) => isModal ? 'unset' : '66px');
const HeaderWrapper = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "RealTimeAudioPanel__HeaderWrapper",
  componentId: "sc-nq05d4-1"
})(["height:57px;justify-content:center;align-items:center;position:absolute;top:0;z-index:1;"]);
const BodyWrapper = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "RealTimeAudioPanel__BodyWrapper",
  componentId: "sc-nq05d4-2"
})(["flex-grow:1;justify-content:center;align-items:center;flex-direction:column;"]);
const MarkerPanel = /*#__PURE__*/styled(Box).withConfig({
  displayName: "RealTimeAudioPanel__MarkerPanel",
  componentId: "sc-nq05d4-3"
})(["overflow-y:auto;position:relative;"]);
const FooterWrapper = /*#__PURE__*/styled(Absolute).withConfig({
  displayName: "RealTimeAudioPanel__FooterWrapper",
  componentId: "sc-nq05d4-4"
})(["bottom:16px;height:48px;width:100%;background:transparent;"]);
const ControlWrapper = /*#__PURE__*/styled(Flex).withConfig({
  displayName: "RealTimeAudioPanel__ControlWrapper",
  componentId: "sc-nq05d4-5"
})(["align-items:center;justify-content:space-between;height:48px;"]);
const Mic = /*#__PURE__*/styled(Box).withConfig({
  displayName: "RealTimeAudioPanel__Mic",
  componentId: "sc-nq05d4-6"
})(["cursor:pointer;border-radius:50%;width:45px;height:45px;background:transparent;display:flex;align-items:center;justify-content:center;"]);
const Close = /*#__PURE__*/styled(Mic).withConfig({
  displayName: "RealTimeAudioPanel__Close",
  componentId: "sc-nq05d4-7"
})(["background:transparent;"]);
const ChildWrapper = /*#__PURE__*/styled(Box).withConfig({
  displayName: "RealTimeAudioPanel__ChildWrapper",
  componentId: "sc-nq05d4-8"
})(["height:100%;display:flex;position:absolute;bottom:10%;"]);
/**
 * AudioRecorder component handles real-time audio recording and playback functionality
 * @internal
 */
const RealTimeAudioPanel = ({
  realtimeContext,
  locale,
  requestFrom,
  isModal = false
}) => {
  var _a, _b;
  const {
    setLoadingStatus
  } = useLoadingStateContext();
  const {
    setStartRealTime
  } = useRealTimeContext();
  const {
    setChatMessages
  } = useChatMessagesContext();
  const pennysid = (_a = browserStorage.getItem('pennysid')) !== null && _a !== void 0 ? _a : '';
  const {
    isMuted,
    messages,
    marker,
    mediaStream,
    startChat,
    stopChat,
    toggleMute
  } = useRealTimeChatCoordinator({
    locale,
    realtimeContext,
    analytics,
    baseUrl: window.location.origin,
    onLoadingChange: setLoadingStatus,
    onOpenStateChange: setStartRealTime,
    pennysid,
    requestFrom
  });
  useEffect(() => {
    startChat();
  }, []);
  useEffect(() => {
    setChatMessages(messages);
  }, [messages]);
  function handleStopChatClick() {
    stopChat();
    setStartRealTime(false);
  }
  return React.createElement(Container, {
    isModal: isModal,
    "data-testid": 'real-time-audio-panel'
  }, React.createElement(Flex, {
    flexDirection: 'column'
  }, React.createElement(BodyWrapper, null, React.createElement(HeaderWrapper, null, React.createElement(RealTimeHeader, null)), ((_b = marker === null || marker === void 0 ? void 0 : marker.markerItem) === null || _b === void 0 ? void 0 : _b.displayComponentName) ? React.createElement(MarkerPanel, {
    "data-testid": 'marker-panel'
  }, React.createElement(RenderMarkers, {
    markerToDisplay: marker,
    "data-testid": 'marker-display'
  })) : React.createElement(ChildWrapper, null, React.createElement(BotWithAnimatedBorder, {
    isMuted: isMuted,
    mediaStream: mediaStream
  })), React.createElement(FooterWrapper, null, React.createElement(ControlWrapper, {
    px: 3
  }, React.createElement(Mic, {
    onClick: toggleMute,
    "data-testid": 'toggle-mute'
  }, isMuted ? React.createElement(Relative, {
    top: '3px'
  }, React.createElement(MicOff, {
    size: '30px',
    color: '#fff',
    "data-testid": 'mute-enabled'
  })) : React.createElement(Relative, {
    top: '3px'
  }, React.createElement(Microphone, {
    size: '30px',
    color: '#fff',
    "data-testid": 'mute-disabled'
  }))), React.createElement(Close, {
    onClick: handleStopChatClick,
    "data-testid": 'close-chat'
  }, React.createElement(CloseIcon, {
    size: '30px',
    color: '#fff'
  })))))));
};
export default React.memo(RealTimeAudioPanel);